import type { SidebarLink } from '@monecolier/ui';

/**
 * UX-85 — SEULE définition des entrées de menu de web-admin (route, libellé,
 * icône), consommée par :
 * - `components/Sidebar.tsx`, qui décide QUELLES entrées afficher et dans quel
 *   groupe selon les rôles ;
 * - `breadcrumbs.ts`, qui s'en sert comme maillons parents du fil d'Ariane.
 *
 * Extraite de la Sidebar (où ces littéraux vivaient en ligne) pour que
 * renommer une entrée — comme « Classes » → « Gestion pédagogique » (SCH-66) —
 * renomme aussi le maillon, au lieu de laisser une seconde copie diverger. Les
 * commentaires qui justifient la PLACE et la GARDE de chaque entrée restent
 * dans Sidebar.tsx, à côté de la décision qu'ils expliquent.
 */
export const NAV = {
  home: { to: '/', label: 'Accueil', icon: '🏠' },
  account: { to: '/account', label: 'Compte', icon: '👤' },

  // Élèves & Inscriptions
  studentsOverview: { to: '/students-overview', label: "Vue d'ensemble", icon: '📊' },
  students: { to: '/students', label: 'Liste des élèves', icon: '🎓' },
  enrollments: { to: '/enrollments', label: 'Inscriptions', icon: '📝' },

  // Enseignant
  teacherHome: { to: '/teacher-home', label: 'Accueil enseignant', icon: '📚' },
  teacherClasses: { to: '/teacher-classes', label: 'Classe', icon: '🏫' },
  teacherSchedule: { to: '/teacher-schedule', label: 'Emploi du temps', icon: '🗓️' },
  teacherAttendance: { to: '/teacher-attendance', label: 'Présence', icon: '✅' },
  teacherHomework: { to: '/teacher-homework', label: 'Devoirs', icon: '📚' },
  teacherGrades: { to: '/teacher-grades', label: 'Notes', icon: '📝' },
  teacherMessaging: { to: '/teacher-messaging', label: 'Messagerie', icon: '💬' },

  // Comptabilité
  // En-tête cliquable du groupe (`headerTo`, UX-46), sans icône propre.
  accountingOverview: { to: '/accounting-overview', label: 'Comptabilité' },
  financialDashboard: { to: '/financial-dashboard', label: 'Dashboard financier', icon: '📊' },
  financialByClass: { to: '/financial-by-class', label: 'Par classe', icon: '🏫' },
  overdueInvoices: { to: '/overdue-invoices', label: 'Factures impayées', icon: '⚠️' },
  payments: { to: '/payments', label: 'Caisse', icon: '💰' },
  paymentHistory: { to: '/payment-history', label: 'Historique des encaissements', icon: '📜' },
  paymentProofs: { to: '/payment-proofs', label: 'Preuves de paiement', icon: '📎' },
  creditLedger: { to: '/credit-ledger', label: 'Solde créditeur familial', icon: '🏦' },
  fees: { to: '/fees', label: 'Tarifs', icon: '💵' },

  // Paramètres
  settings: { to: '/settings', label: 'Aperçu', icon: '⚙️' },
  onboarding: { to: '/onboarding', label: 'Assistant de configuration', icon: '🧭' },
  branding: { to: '/branding', label: 'Personnalisation', icon: '🎨' },
  classes: { to: '/classes', label: 'Gestion pédagogique', icon: '🏫' },
  schoolYears: { to: '/school-years', label: 'Années scolaires', icon: '📅' },
  paymentMethods: { to: '/payment-methods', label: 'Moyens de paiement', icon: '💳' },
  documentRequirements: { to: '/document-requirements', label: 'Documents requis', icon: '📄' },
  invitations: { to: '/invitations', label: 'Équipe', icon: '👥' },
  whatsappBroadcasts: { to: '/whatsapp-broadcasts', label: 'Diffusion WhatsApp', icon: '💬' },
  mfaSetup: { to: '/mfa-setup', label: 'Authentification à deux facteurs', icon: '🔒' },
  auditLogs: { to: '/audit-logs', label: "Journal d'audit", icon: '📋' },
} as const satisfies Record<string, SidebarLink>;

/** Libellés d'en-tête de groupe de la Sidebar. */
export const NAV_GROUP_LABELS = {
  students: 'Élèves & Inscriptions',
  teacher: 'Enseignant',
  accounting: 'Comptabilité',
  settings: 'Paramètres',
} as const;
